import { EmailVerificationKey, VerifyEmailParams } from '@gyym/sdk'
import { useGyymClient } from '@gyym/sdk-react'
import { useDialog, useModal } from '@gyym/ui'
import { useRouter } from 'next/router'
import React from 'react'

import { PasskeyPromotionModal } from '@/modules/Passkey/components/PasskeyPromotionModal'
import {
  consumePasskeyPromotionPending,
  isPasskeySupported,
} from '@/modules/Passkey/utils'

import { useAuthUser } from './useAuthUser'

/**
 * Check if the query has all the required params to verify an email
 */
export const hasVerifyEmailQuery = (
  params: Partial<VerifyEmailParams & { key: EmailVerificationKey }>,
): boolean => {
  return Boolean(params.key && params.expires && params.signature)
}

export const useVerifyEmailEffect = () => {
  const { isLoggedIn, isEmailVerified, revalidate, isValidating } =
    useAuthUser()

  const router = useRouter()
  const client = useGyymClient()
  const { openDialog } = useDialog()
  const { openModal } = useModal()

  React.useEffect(() => {
    if (isValidating) return

    const isVerifyEmailQuery = hasVerifyEmailQuery(router.query)
    if (!isVerifyEmailQuery) return

    if (!isLoggedIn) {
      router.push(
        `/login?key=${router.query.key}&expires=${router.query.expires}&signature=${router.query.signature}`,
      )
      return
    }

    if (isEmailVerified) return

    // Verify user and revalidate
    const exec = async () => {
      await client.auth.user.verifyEmail(
        Number(router.query.key) as EmailVerificationKey,
        {
          expires: Number(router.query.expires),
          signature: router.query.signature as string,
        },
      )

      const authUser = await revalidate()

      // 会員登録完了と同時に訴求するため、ログイン時の訴求フラグは消費しておく
      consumePasskeyPromotionPending()

      // パスキー対応環境では登録完了ダイアログとしてパスキー登録を訴求する
      if (
        isPasskeySupported() &&
        authUser &&
        !authUser.hasPasskey &&
        !authUser.isPasskeyPromotionHidden
      ) {
        openModal(PasskeyPromotionModal, { context: 'registration' })
        return
      }

      openDialog({
        title: '新規登録が完了しました。',
        body: (
          <>
            ご登録ありがとうございます！
            <br />
            引き続き、施設をご利用いただくために必要なプロフィールをご登録ください。
          </>
        ),
      })
    }

    exec()
  }, [
    client.auth.user,
    isEmailVerified,
    isLoggedIn,
    isValidating,
    openDialog,
    openModal,
    revalidate,
    router,
  ])
}
